import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Interweave from 'interweave'
import { isEmpty } from 'lodash'
import { convertMdToHtml } from '..'
import { SUGGESTION_HTML_ID_ATTRIBUTE, UPDATE_STATUS } from '../../../constants'
import { getStatusLabelType } from '../utils'
import Section from '../../Section'
import Link from '../../Link'
import Alert from '../../Alert'
import Badge from '../../Badge'

const SuggestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
`

const SuggestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;

  @media screen and (max-width: 1200px) {
    flex-direction: column;
    align-items: baseline;
  }
`

const SuggestionHeaderDetails = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`

const SuggestionTitle = styled.h3`
  font-weight: 600;
  font-size: 24px;
  line-height: 30px;

  ${Link}, p {
    display: inline-block;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`

const Suggestions = ({ report: { reportContent } }) => {
  return (
    <Section title="Auditor Suggestions">
      {!isEmpty(reportContent.breakdownAndAutomatedAnalysis.suggestionsArray) &&
        reportContent.breakdownAndAutomatedAnalysis.suggestionsArray.map((vulnerability, index) => {
          const findingId = index + 1

          return (
            <SuggestionContainer key={findingId} id={`${SUGGESTION_HTML_ID_ATTRIBUTE}${findingId}`}>
              <SuggestionHeader>
                <SuggestionTitle>
                  <Link href={`#${SUGGESTION_HTML_ID_ATTRIBUTE}${findingId}`}>S{findingId}</Link>
                  &nbsp;&nbsp;
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      vulnerability.title.label !== 'Other'
                        ? vulnerability.title.label
                        : vulnerability.otherTitle,
                    )}
                  />
                </SuggestionTitle>
                <SuggestionHeaderDetails>
                  {vulnerability.status ? (
                    <Badge type={getStatusLabelType(vulnerability.status.value)}>
                      {vulnerability.status.label}
                    </Badge>
                  ) : null}
                </SuggestionHeaderDetails>
              </SuggestionHeader>
              {vulnerability.updates?.length &&
                vulnerability.updates.map((update, i) =>
                  !isEmpty(update.description) ? (
                    <div key={i}>
                      <Alert type={update.type?.value}>
                        <b>{update.type?.value === UPDATE_STATUS[2].value ? 'Alert' : 'Update'}</b>
                        <Interweave content={convertMdToHtml.makeHtml(update.description)} />
                      </Alert>
                    </div>
                  ) : null,
                )}
              {!isEmpty(vulnerability?.contracts?.[0]?.name) && (
                <div>
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      '**File(s) affected:** ' +
                        vulnerability.contracts.map(c => (c.name ? ` \`${c.name}\`` : '')),
                    )}
                  />
                </div>
              )}
              {!isEmpty(vulnerability.relatedIssues[0]?.swc) && (
                <div>
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      '**Related Issue(s):** ' +
                        vulnerability.relatedIssues.map(i =>
                          i.swc
                            ? ` [SWC-${i.swc}](https://smartcontractsecurity.github.io/SWC-registry/docs/SWC-${i.swc})`
                            : '',
                        ),
                    )}
                  />
                </div>
              )}
              {!isEmpty(vulnerability.description) && (
                <div>
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      '**Description:** ' + vulnerability.description,
                    )}
                  />
                </div>
              )}
              {!isEmpty(vulnerability.exploitScenario) && (
                <div>
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      '**Exploit Scenario:** <br>' + vulnerability.exploitScenario,
                    )}
                  />
                </div>
              )}
              {!isEmpty(vulnerability.recommendation) && (
                <div>
                  <Interweave
                    content={convertMdToHtml.makeHtml(
                      '**Recommendation:** ' + vulnerability.recommendation,
                    )}
                  />
                </div>
              )}
            </SuggestionContainer>
          )
        })}
    </Section>
  )
}

Suggestions.propTypes = {
  report: PropTypes.object,
}

export default Suggestions
